/**
 * Match Session Script Domain
 *
 * Manages the server-controlled session script state during the 'playing' phase.
 * Session scripts replace the old script system with a trigger-driven sequence
 * of steps defined in JSON files on the server.
 *
 * The server sends:
 *   - OpCodeSessionScriptStep (320): on phase entry, step start, or step completion
 *   - OpCodeVoiceChatControl (321): when voice chat enabled state changes
 *
 * A step's name is resolved via i18n as
 * `sessionScripts.<sessionScriptId>.steps.<stepId>` (stepName, below).
 *
 * When no session script is active (session_script_id == null), features are all enabled
 * and the match runs until its scheduled end time.
 */

import type { TFunction } from 'i18next';
import { create } from 'zustand';
import type { SessionScriptStepPayload, SessionScriptFeatureState, SessionScriptStepRemainder, StepTrigger } from '../constants/op-codes';
import { handleSessionScriptIdentified } from './dialogue-lines';
import { openDialogue } from './dialogue';
import { handleStepStarted } from './team-activity';

// ── Store ──────────────────────────────────────────────────────────────────

const DEFAULT_FEATURES: SessionScriptFeatureState = {
  voiceChatEnabled: true,
  quarterProcessingEnabled: true,
  lockedSections: [],
  cashEnabled: true,
  influenceEnabled: true,
  missionsVisible: true,
  missionOverrideText: '',
  missionOverrideDescription: '',
};

interface MatchSessionScriptState {
  /** The session script ID currently running, or null if no session script. */
  sessionScriptId: string | null;
  /** The current step ID, or null (no session script or complete). */
  stepId: string | null;
  /** 0-based index of the current step. -1 = complete. */
  stepIndex: number;
  /** Total number of steps in the session script. */
  stepCount: number;
  /** Current step state: "pending", "active", "completed", or null. */
  stepState: 'pending' | 'active' | 'completed' | null;
  /** True when the session script has finished all steps. */
  complete: boolean;
  /** Active feature flags sent by the server. */
  features: SessionScriptFeatureState;
  /** UTC Unix timestamp when this step started (0 = unknown). */
  stepStartedAtUtc: number;
  /** Whether a session_script_step message has been received from the server. */
  sessionScriptReceived: boolean;

  /** End trigger for the current step (used for countdown display). */
  endTrigger: StepTrigger | null;

  /**
   * Remaining steps (current + future) from the last server payload.
   * Used to estimate wall-clock time remaining in the session script.
   */
  remainingSteps: SessionScriptStepRemainder[];

  /**
   * Session tick counter (1-based, matches server's SessionTicks).
   * Updated on every session_script_step, timer_sync, and financial_report.
   */
  sessionTicks: number;

  /** Apply a session_script_step payload from the server. */
  setSessionScriptStep(payload: SessionScriptStepPayload): void;
  /** Update session ticks (called on every financial_report / timer_sync). */
  setSessionTicks(sessionTicks: number): void;
  /** Reset to default (no session script) state when leaving the match. */
  reset(): void;
}

export const useMatchSessionScriptStore = create<MatchSessionScriptState>((set) => ({
  sessionScriptId: null,
  stepId: null,
  stepIndex: 0,
  stepCount: 0,
  stepState: null,
  complete: false,
  features: DEFAULT_FEATURES,
  stepStartedAtUtc: 0,
  sessionScriptReceived: false,
  endTrigger: null,
  remainingSteps: [],
  sessionTicks: 0,

  setSessionScriptStep: (payload) =>
    set((state) => ({
      sessionScriptId: payload.session_script_id,
      stepId: payload.step_id,
      stepIndex: payload.step_index,
      stepCount: payload.step_count ?? 0,
      stepState: payload.step_state ?? null,
      complete: payload.complete,
      features: payload.features ?? DEFAULT_FEATURES,
      stepStartedAtUtc: payload.step_started_at_utc ?? 0,
      sessionScriptReceived: true,
      endTrigger: payload.end_trigger ?? null,
      remainingSteps: payload.remaining_steps ?? [],
      sessionTicks: payload.session_ticks ?? state.sessionTicks,
    })),

  setSessionTicks: (sessionTicks) =>
    set({ sessionTicks }),

  reset: () =>
    set({
      sessionScriptId: null,
      stepId: null,
      stepIndex: 0,
      stepCount: 0,
      stepState: null,
      complete: false,
      features: DEFAULT_FEATURES,
      stepStartedAtUtc: 0,
      sessionScriptReceived: false,
      endTrigger: null,
      remainingSteps: [],
      sessionTicks: 0,
    }),
}));

/**
 * A step of the session has locked spending the team's cash: every spend
 * rests until it lifts (SpendButton), and the server refuses one sent anyway
 * (spending-lock.go).
 */
export function useSpendingLocked(): boolean {
  return useMatchSessionScriptStore((s) => !s.features.cashEnabled);
}

/**
 * A step of the session has locked investing the team's Influence in cities:
 * the investment rests with a lock until it lifts, and the server refuses one
 * sent anyway.
 */
export function useInfluenceLocked(): boolean {
  return useMatchSessionScriptStore((s) => !s.features.influenceEnabled);
}

// ── Handler ────────────────────────────────────────────────────────────────

/**
 * Handle an incoming session_script_step message from the server.
 * Called by domain-message-router when OpCodeSessionScriptStep (320) is received.
 */
export function handleSessionScriptStep(data: SessionScriptStepPayload): void {
  const store = useMatchSessionScriptStore.getState();
  const payload = data;

  // Detect when a step transitions to active so we can trigger any associated dialogue.
  // We compare before updating the store so we only fire on the transition, not on
  // every repeated broadcast of the same active step.
  const isNewlyActive =
    payload.step_state === 'active' &&
    (store.stepState !== 'active' || store.stepId !== payload.step_id);

  store.setSessionScriptStep(payload);

  // Trigger voice-line audio download on the first session script step received
  if (payload.session_script_id && !store.sessionScriptReceived) {
    handleSessionScriptIdentified(payload.session_script_id);
  }

  if (isNewlyActive && payload.dialogue_id) {
    const dialogueId = payload.dialogue_id;
    openDialogue(dialogueId);
  }

  // Clear any stale activity completion flag from the previous step so that the
  // "waiting for other teams" overlay does not appear at the start of a new step.
  if (isNewlyActive) {
    handleStepStarted();
  }
}

/**
 * The session's ticks as another domain's message carries them — the
 * financial report and the timer sync (financials.ts) — so the quarter shown
 * against the session stays current.
 */
export function handleSessionTicks(sessionTicks: number): void {
  useMatchSessionScriptStore.getState().setSessionTicks(sessionTicks);
}

/**
 * A step's name in the player's language — the quarter's card, the next
 * meeting, the team activity's sheet — or its id where the script names none.
 */
export function stepName(t: TFunction, scriptId: string, stepId: string): string {
  return t(`sessionScripts.${scriptId}.steps.${stepId}`, { defaultValue: stepId });
}
